<template>
  <div class="par-store-page">
    <MainNavbar />

    <div class="par-store">
      <header class="par-store-hero">
        <p class="par-store-kicker">
          <i class="fa-solid fa-universal-access" aria-hidden="true" />
          The Able · Adaptive gear
        </p>
        <h1 class="par-store-title">Paralympic Equipment Store</h1>
        <p class="par-store-lead">
          Wheelchairs, RaceRunners, boccia ramps, adaptive rackets, and more—sourced for
          Kenyan schools, clubs, and athletes.
        </p>
      </header>

      <EquipmentToolbar
        v-model:search="search"
        v-model:category-code="categoryCode"
        v-model:type-code="typeCode"
        v-model:in-stock-only="inStockOnly"
        v-model:needs-fitting="needsFitting"
        v-model:fitter-only="fitterOnly"
        :result-count="filteredItems.length"
      />

      <div v-if="loading" class="par-loading" aria-live="polite">
        <i class="fa-solid fa-spinner fa-spin fa-lg" aria-hidden="true" />
        <span>Loading equipment…</span>
      </div>

      <p v-else-if="loadError" class="par-empty" role="alert">{{ loadError }}</p>

      <div v-else-if="filteredItems.length" class="par-grid">
        <EquipmentCard
          v-for="(item, index) in filteredItems"
          :key="item.item_id"
          :item="item"
          :index="index"
          @learn-more="goToDetail"
          @make-order="onMakeOrder"
        />
      </div>

      <div v-else class="par-empty">
        <p v-if="hasActiveFilters">No equipment matches your filters. Try clearing a filter or search term.</p>
        <p v-else>No paralympic equipment is listed yet. Check back soon.</p>
      </div>
    </div>

    <MainFooter />
  </div>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import MainFooter from "@/components/main/headernfooter/Footer.vue";
import EquipmentCard from "@/components/paralympic/EquipmentCard.vue";
import EquipmentToolbar from "@/components/paralympic/EquipmentToolbar.vue";
import { buildApiUrl } from "@/config/api";
import globalToastMixin from "@/mixins/globalToastMixin";
import { PARALYMPIC_API, PARALYMPIC_DETAIL_ROUTE } from "@/routes/paralympic/constants";

export default {
  name: "ParalympicStore",
  components: { MainNavbar, MainFooter, EquipmentCard, EquipmentToolbar },
  mixins: [globalToastMixin],
  data() {
    return {
      items: [],
      loading: true,
      loadError: "",
      search: "",
      categoryCode: null,
      typeCode: null,
      inStockOnly: false,
      needsFitting: false,
      fitterOnly: false,
      searchDebounce: null,
    };
  },
  computed: {
    filteredItems() {
      let rows = [...this.items];

      if (this.inStockOnly) {
        rows = rows.filter((row) => row.is_in_stock);
      }
      if (this.needsFitting) {
        rows = rows.filter((row) => row.needs_fitting);
      }
      if (this.fitterOnly) {
        rows = rows.filter((row) => row.fitter_only);
      }

      const q = (this.search || "").trim().toLowerCase();
      if (q) {
        rows = rows.filter((row) => {
          const hay = [
            row.name,
            row.brand,
            row.short_description,
            row.category,
            row.type,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        });
      }

      return rows;
    },
    hasActiveFilters() {
      return Boolean(
        this.search?.trim()
        || this.categoryCode
        || this.typeCode
        || this.inStockOnly
        || this.needsFitting
        || this.fitterOnly,
      );
    },
  },
  watch: {
    categoryCode() {
      this.typeCode = null;
      this.fetchItems();
    },
    typeCode() {
      this.fetchItems();
    },
    search() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => {
        this.fetchItems();
      }, 350);
    },
    inStockOnly() {
      this.fetchItems();
    },
    needsFitting() {
      this.fetchItems();
    },
    fitterOnly() {
      this.fetchItems();
    },
  },
  mounted() {
    this.fetchItems();
  },
  beforeUnmount() {
    clearTimeout(this.searchDebounce);
  },
  methods: {
    async fetchItems() {
      this.loading = true;
      this.loadError = "";
      try {
        const params = new URLSearchParams();
        if (this.search?.trim()) params.set("search", this.search.trim());
        if (this.categoryCode) params.set("category_code", String(this.categoryCode));
        if (this.typeCode) params.set("type_code", String(this.typeCode));
        if (this.inStockOnly) params.set("in_stock", "true");
        if (this.needsFitting) params.set("needs_fitting", "true");
        if (this.fitterOnly) params.set("fitter_only", "true");

        const qs = params.toString();
        const url = buildApiUrl(PARALYMPIC_API.list + (qs ? `?${qs}` : ""));
        const res = await fetch(url);
        const data = await res.json().catch(() => ({}));
        if (!res.ok) {
          throw new Error(data.detail || "Could not load equipment.");
        }
        this.items = Array.isArray(data.items) ? data.items : [];
      } catch (err) {
        this.loadError = err.message || "Could not load equipment.";
        this.items = [];
      } finally {
        this.loading = false;
      }
    },
    goToDetail(item) {
      this.$router.push({
        name: PARALYMPIC_DETAIL_ROUTE,
        params: { slug: item.slug },
      });
    },
    onMakeOrder(item) {
      this.showToast(
        "Orders coming soon",
        `Ordering for “${item.name}” will open in a future update.`,
        "info",
      );
    },
  },
};
</script>

<style scoped src="@/components/paralympic/paralympicStore.css"></style>

<style scoped>
.par-store-page {
  min-height: 100vh;
  background: var(--background-color1);
}
</style>
